import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Users, Wifi, Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FindSpace() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useOutletContext();
  const [spaces, setSpaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pageContent, setPageContent] = useState({
    hero_title: 'Find Your Perfect Workspace',
    hero_subtitle: 'Explore our premium locations and discover the ideal space for your business.',
    hero_image_url: ''
  });
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    type: searchParams.get('type') || '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [spacesRes, locationsRes, pageContentRes] = await Promise.all([
          axios.get(`${API}/spaces`),
          axios.get(`${API}/locations`),
          axios.get(`${API}/find-space-page-content`),
        ]);
        setSpaces(spacesRes.data);
        setLocations(locationsRes.data);
        
        // Convert relative URL to full URL for page content image
        const contentData = pageContentRes.data;
        if (contentData.hero_image_url && !contentData.hero_image_url.startsWith('http')) {
          contentData.hero_image_url = `${BACKEND_URL}${contentData.hero_image_url}`;
        }
        setPageContent(contentData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const workspaceTypes = ['Coworking Space', 'Private Office', 'Virtual Office', 'Managed Enterprise Solutions', 'Conference Room'];

  const filteredSpaces = spaces.filter((space) => {
    if (filters.location && filters.location !== 'all') {
      // location_id is now text, so do case-insensitive partial match
      if (!space.location_id || !space.location_id.toLowerCase().includes(filters.location.toLowerCase())) {
        return false;
      }
    }
    if (filters.type && filters.type !== 'all' && space.type !== filters.type) return false;
    return true;
  });

  const handleEnquire = (space) => {
    openModal({
      form_type: 'space_enquiry',
      space_name: space.name,
      workspace_type: space.type,
    });
  };

  return (
    <div className="pt-20" data-testid="find-space-page">
      <SEO page="find-space" />
      {/* Hero */}
      <section 
        className="text-white py-16 sm:py-20 lg:py-24 relative bg-cover bg-center"
        style={{
          backgroundImage: pageContent.hero_image_url 
            ? `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${pageContent.hero_image_url})`
            : 'linear-gradient(135deg, #222 0%, #1a1a1a 100%)'
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{pageContent.hero_title}</h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl">
            {pageContent.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white py-6 border-b sticky top-20 z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select value={filters.location} onValueChange={(val) => setFilters({ ...filters, location: val })}>
                <SelectTrigger data-testid="filter-location">
                  <SelectValue placeholder="Select Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.area}>{loc.area}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={filters.type} onValueChange={(val) => setFilters({ ...filters, type: val })}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="Workspace Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {workspaceTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={() => setFilters({ location: '', type: '' })}
              className="btn-secondary whitespace-nowrap"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <p className="text-[#666]">
              Showing <span className="font-semibold text-[#222]">{filteredSpaces.length}</span> spaces
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredSpaces.map((space, index) => {
              return (
                <div
                  key={space.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md card-hover"
                  data-testid={`space-result-${index}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Image */}
                    <div className="relative h-64 md:h-auto">
                      <img
                        src={space.images[0]}
                        alt={space.name}
                        className="w-full h-full object-cover"
                      />
                      {space.badges.length > 0 && (
                        <div className="absolute top-4 right-4 flex flex-col gap-2">
                          {space.badges.map((badge, idx) => (
                            <span key={idx} className="px-3 py-1 bg-[#E12429] text-white text-xs font-semibold rounded-full">
                              {badge}
                            </span>
                          ))}
                        </div>
                      )}
                      {space.show_virtual_tour && space.virtual_tour_link && (
                        <button
                          onClick={() => window.open(space.virtual_tour_link, '_blank')}
                          className="absolute bottom-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-sm font-semibold hover:bg-white transition-all"
                        >
                          360° Virtual Tour
                        </button>
                      )}
                    </div>

                    {/* Details */}
                    <div className="p-6 flex flex-col">
                      <h3 className="text-2xl font-bold text-[#222] mb-2">{space.name}</h3>
                      <p className="text-sm text-[#E12429] font-semibold mb-3">{space.type}</p>
                      {space.location_id && (
                        <div className="flex items-center space-x-2 text-sm text-[#666] mb-3">
                          <MapPin size={14} />
                          <span>{space.location_id}</span>
                        </div>
                      )}
                      <p className="text-[#666] text-sm mb-4 line-clamp-2">{space.description}</p>
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2 text-sm text-[#666]">
                          <Users size={16} />
                          <span>{space.capacity}</span>
                        </div>
                        {space.price && (
                          <span className="text-[#E12429] font-bold">{space.price}</span>
                        )}
                      </div>

                      {/* Amenities icons */}
                      <div className="flex items-center space-x-3 mb-4 text-[#666]">
                        <Wifi size={16} />
                        <Users size={16} />
                        <Star size={16} />
                      </div>

                      <div className="flex space-x-3 mt-auto">
                        <button
                          onClick={() => navigate(`/space/${space.id}`)}
                          className="flex-1 btn-secondary text-sm py-2"
                        >
                          Know More
                        </button>
                        <button
                          onClick={() => handleEnquire(space)}
                          className="flex-1 btn-primary text-sm py-2"
                          data-testid={`enquire-button-${index}`}
                        >
                          Contact Us
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredSpaces.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#666] text-lg">No spaces found matching your criteria.</p>
              <button
                onClick={() => setFilters({ location: '', type: '' })}
                className="btn-primary mt-4"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}